import { onMounted, onUnmounted, ref } from "vue";
import { injector } from "@/services";
import {
  API_SERVICE_INJECTION_KEY,
  ERROR_SERVICE_INJECTION_KEY,
  ROUTER_SERVICE_INJECTION_KEY,
} from "@/constants";
import { useSystemStore } from "../services/store";
import { HEALTH_CHECK_URL, isHealthCheckUp } from "../utils/health-check.util";
import {
  getCountdownSeconds,
  SERVICE_UNAVAILABLE_RETRY_INTERVAL_SEC,
} from "../utils/service-unavailable-recovery.util";

const TICK_INTERVAL_MS = 1000;

export const useServiceUnavailableRecovery = () => {
  const apiService = injector.injectFromRoot(API_SERVICE_INJECTION_KEY);
  const errorService = injector.injectFromRoot(ERROR_SERVICE_INJECTION_KEY);
  const router = injector.injectFromRoot(ROUTER_SERVICE_INJECTION_KEY);
  const systemStore = useSystemStore();

  const countdownSeconds = ref(SERVICE_UNAVAILABLE_RETRY_INTERVAL_SEC);
  const isChecking = ref(false);

  let tickIntervalId: ReturnType<typeof setInterval> | null = null;
  let retryIntervalId: ReturnType<typeof setInterval> | null = null;
  let elapsedMs = 0;

  const recover = () => {
    systemStore.setHealthStatus(true);
    errorService.unblockDisplay();
    router.push("/");
  };

  const checkHealth = async () => {
    if (isChecking.value) {
      return;
    }

    isChecking.value = true;

    try {
      const response = await apiService.get<{ status: string }[]>(
        HEALTH_CHECK_URL
      );

      if (isHealthCheckUp(response, apiService.isRequestSuccess)) {
        recover();
      }
    } finally {
      isChecking.value = false;
    }
  };

  const startRecovery = () => {
    elapsedMs = 0;
    countdownSeconds.value = SERVICE_UNAVAILABLE_RETRY_INTERVAL_SEC;

    tickIntervalId = setInterval(() => {
      elapsedMs += TICK_INTERVAL_MS;
      countdownSeconds.value = getCountdownSeconds(
        SERVICE_UNAVAILABLE_RETRY_INTERVAL_SEC,
        elapsedMs
      );
    }, TICK_INTERVAL_MS);

    retryIntervalId = setInterval(() => {
      void checkHealth();
    }, SERVICE_UNAVAILABLE_RETRY_INTERVAL_SEC * 1000);

    void checkHealth();
  };

  const stopRecovery = () => {
    if (tickIntervalId) {
      clearInterval(tickIntervalId);
      tickIntervalId = null;
    }

    if (retryIntervalId) {
      clearInterval(retryIntervalId);
      retryIntervalId = null;
    }
  };

  onMounted(startRecovery);
  onUnmounted(stopRecovery);

  return {
    countdownSeconds,
    isChecking,
    checkHealth,
  };
};
